import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { ArrowRight, ChevronRight, GraduationCap, X } from 'lucide-react';
import { dashboardPanel, dashboardPanelFeatured } from './dashboardStyles';
import type { DemoCategory } from '../../data/demoGraphs';
import { DashboardIcon } from './DashboardIcon';
import { getCategoryConfig, stripEmoji } from './dashboardIconConfig';

export const START_HERE_DISMISS_KEY = 'cryptoflow-start-here-dismissed';

export type StartHereScenario = {
    id: string;
    title: string;
    startHereBlurb?: string;
    startHereDuration?: string;
    startHereOrder?: number;
    category: DemoCategory;
};

type Props = {
    scenarios: StartHereScenario[];
    onScenarioClick: (scenario: StartHereScenario) => void;
    onDismiss?: () => void;
};

const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export const StartHereSection: React.FC<Props> = ({ scenarios, onScenarioClick, onDismiss }) => {
    const [dismissed, setDismissed] = useState(true);

    useEffect(() => {
        setDismissed(localStorage.getItem(START_HERE_DISMISS_KEY) === '1');
    }, []);

    const handleDismiss = () => {
        localStorage.setItem(START_HERE_DISMISS_KEY, '1');
        setDismissed(true);
        onDismiss?.();
    };

    if (dismissed || scenarios.length === 0) {
        return null;
    }

    const sorted = [...scenarios].sort(
        (a, b) => (a.startHereOrder ?? 99) - (b.startHereOrder ?? 99),
    );

    return (
        <section
            data-testid="dashboard-start-here"
            aria-labelledby="start-here-heading"
            className={clsx('rounded-2xl p-6 sm:p-7 mb-6 bg-brand-cream/30 dark:bg-card/90', dashboardPanelFeatured)}
            data-tour="dashboard-start-here"
        >
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2.5 mb-1.5">
                        <DashboardIcon variant="tile" tone="tutorials" icon={GraduationCap} />
                        <h2 id="start-here-heading" className="text-lg font-semibold tracking-tight">
                            Start here
                        </h2>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-prose pl-[2.625rem]">
                        New to CryptoFlow? Complete these 3 scenarios (~25 min).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Dismiss guided path — I've used the platform before"
                    className={clsx(
                        'text-xs text-muted-foreground hover:text-foreground whitespace-nowrap shrink-0',
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 -mr-1 -mt-0.5',
                        'hover:bg-muted/60 transition-colors',
                        focusRing,
                    )}
                >
                    <X className="w-3.5 h-3.5" aria-hidden />
                    I&apos;ve used the platform before
                </button>
            </div>

            <ol className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 list-none p-0 m-0">
                {sorted.map((scenario, index) => {
                    const stepNum = scenario.startHereOrder ?? index + 1;
                    const isLast = index === sorted.length - 1;
                    const categoryConfig = getCategoryConfig(scenario.category);
                    const displayTitle = stripEmoji(scenario.title);

                    return (
                        <li key={scenario.id} className="relative flex min-w-0">
                            {!isLast && (
                                <div
                                    className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 items-center pointer-events-none"
                                    aria-hidden
                                >
                                    <div className="w-2 h-px bg-border" />
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted/80 dark:bg-muted/40 border border-border/80">
                                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                    </span>
                                </div>
                            )}
                            <button
                                type="button"
                                data-testid={`start-here-step-${stepNum}`}
                                aria-label={`Step ${stepNum}: ${displayTitle}${scenario.startHereDuration ? `, ${scenario.startHereDuration}` : ''}`}
                                onClick={() => onScenarioClick(scenario)}
                                className={clsx(
                                    'group flex flex-col flex-1 min-w-0 text-left',
                                    'rounded-xl p-4 sm:p-5',
                                    dashboardPanel,
                                    'hover:border-primary/40 hover:shadow-xl',
                                    'active:scale-[0.99] transition-all duration-200',
                                    focusRing,
                                )}
                            >
                                <div className="flex items-center justify-between gap-2 mb-3">
                                    <span className="inline-flex items-center text-xs font-semibold font-mono px-2.5 py-0.5 rounded-full bg-primary/10 text-primary dark:bg-primary/15 dark:text-primary">
                                        Step {stepNum}
                                    </span>
                                    {scenario.startHereDuration && (
                                        <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground tabular-nums">
                                            {scenario.startHereDuration}
                                        </span>
                                    )}
                                </div>
                                <DashboardIcon
                                    variant="tile"
                                    tone={categoryConfig.tone}
                                    icon={categoryConfig.icon}
                                    className="mb-3 w-fit"
                                />
                                <h3 className="font-semibold text-sm mb-1.5 leading-snug group-hover:text-primary transition-colors">
                                    {displayTitle}
                                </h3>
                                {scenario.startHereBlurb && (
                                    <p className="text-xs text-muted-foreground line-clamp-2 flex-1 mb-4 leading-relaxed">
                                        {scenario.startHereBlurb}
                                    </p>
                                )}
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary mt-auto group-hover:gap-1.5 transition-[gap]">
                                    Start scenario
                                    <ArrowRight className="w-3.5 h-3.5" aria-hidden />
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
};
